import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import type { AppState, ProgressState, Skill } from '../types';
import { applyCalendarSnapshot } from './calendar';

const KEY = 'english-forge-state-v3';
const LEGACY_KEY = 'english-forge-state-v1';
const LEGACY_KEY_V2 = 'english-forge-state-v2';

const skillAttempts = (): Record<Skill, number> => ({
  vocabulary: 0,
  grammar: 0,
  listening: 0,
  reading: 0,
  pronunciation: 0,
});

export const defaultState = (): AppState => ({
  version: 3,
  profile: {
    name: '',
    goal: 'Kundalik ingliz tili',
    level: 'Pre-A1',
    studyTime: '21:00',
    dailyMinutes: 20,
    timezone: 'Asia/Samarkand',
    onboardingComplete: false,
    createdAt: new Date().toISOString(),
  },
  progress: {
    day: 1,
    streak: 0,
    masteryStreak: 0,
    xp: 0,
    rank: 'Starter',
    completedDays: [],
    dailyCompletionDates: [],
    todayMissionCompleted: false,
    missedDays: 0,
    courseFinished: false,
    totalAnswers: 0,
    correctAnswers: 0,
    words: {},
    grammarMastery: {},
    listeningMastery: 0,
    readingMastery: 0,
    pronunciationMastery: 0,
    reviewDebt: 0,
    recoveryMode: false,
    failedLessonAttempts: 0,
    lessonSessions: [],
    skillAttempts: skillAttempts(),
  },
  settings: {
    theme: 'system',
    strictTeacher: true,
    sound: true,
    haptics: true,
  },
});

function migrate(rawState: Partial<AppState>): AppState {
  const base = defaultState();
  const progress = (rawState.progress ?? {}) as Partial<ProgressState>;
  return {
    ...base,
    ...rawState,
    version: 3,
    profile: { ...base.profile, ...(rawState.profile ?? {}) },
    progress: {
      ...base.progress,
      ...progress,
      words: progress.words ?? {},
      grammarMastery: progress.grammarMastery ?? {},
      completedDays: progress.completedDays ?? [],
      dailyCompletionDates: progress.dailyCompletionDates ?? [],
      todayMissionCompleted: Boolean(progress.todayMissionCompleted ?? false),
      missedDays: Number(progress.missedDays ?? 0),
      courseFinished: Boolean(progress.courseFinished ?? false),
      lessonSessions: progress.lessonSessions ?? [],
      skillAttempts: { ...base.progress.skillAttempts, ...(progress.skillAttempts ?? {}) },
      reviewDebt: Number(progress.reviewDebt ?? 0),
      recoveryMode: Boolean(progress.recoveryMode ?? false),
      failedLessonAttempts: Number(progress.failedLessonAttempts ?? 0),
    },
    settings: { ...base.settings, ...(rawState.settings ?? {}) },
  };
}

async function readKey(key: string) {
  return Capacitor.isNativePlatform()
    ? (await Preferences.get({ key })).value
    : localStorage.getItem(key);
}

export async function loadState(): Promise<AppState> {
  try {
    const raw = (await readKey(KEY)) ?? (await readKey(LEGACY_KEY_V2)) ?? (await readKey(LEGACY_KEY));
    if (!raw) return defaultState();
    return applyCalendarSnapshot(migrate(JSON.parse(raw) as Partial<AppState>));
  } catch {
    return defaultState();
  }
}

export async function saveState(state: AppState) {
  const normalized = applyCalendarSnapshot(state);
  const raw = JSON.stringify({ ...normalized, version: 3 });
  if (Capacitor.isNativePlatform()) await Preferences.set({ key: KEY, value: raw });
  else localStorage.setItem(KEY, raw);
}

export async function clearState() {
  if (Capacitor.isNativePlatform()) {
    await Preferences.remove({ key: KEY });
    await Preferences.remove({ key: LEGACY_KEY_V2 });
    await Preferences.remove({ key: LEGACY_KEY });
  } else {
    localStorage.removeItem(KEY);
    localStorage.removeItem(LEGACY_KEY_V2);
    localStorage.removeItem(LEGACY_KEY);
  }
}
